import { applyProductFamilies } from '#bv/data/productFamilies'
import {
  SERVER_BOOST_3M_PRODUCT_ID,
  SERVER_BOOST_PRODUCT_ID,
  SERVER_BOOST_PRODUCT_IDS,
} from '#bv/data/serverBoostIds'
import { SPOTIFY_1M_SHARED_PRODUCT_ID } from '#bv/data/storefrontProductIds'
import { productImagePath } from '#bv/lib/productImageSlug'

export {
  SERVER_BOOST_3M_PRODUCT_ID,
  SERVER_BOOST_PRODUCT_ID,
  SERVER_BOOST_PRODUCT_IDS,
} from '#bv/data/serverBoostIds'
export { SPOTIFY_1M_SHARED_PRODUCT_ID } from '#bv/data/storefrontProductIds'

export { productImagePath, productImageSlug, productBoxTitle } from '#bv/lib/productImageSlug'
import { buildFollowerTiers, minSmmPackagePrice } from '#bv/lib/smmTiers'
import {
  buildSharedAccessProductCopy,
  isSharedAccessListing,
  usesCustomSharedAccessCopy,
} from '#bv/lib/sharedAccountListing'

/** One row inside a shop bundle picker (links to an existing product id). */
export interface BundlePickerTier {
  label: string
  productId: number
  price: number
  blurb?: string
}

export type SmmPackage = {
  label: string
  quantity: number
  price: number
}

export type SmmTrustBadge = { label: string }

export type SmmPlatform =
  | 'tiktok'
  | 'instagram'
  | 'youtube'
  | 'twitter'
  | 'facebook'
  | 'telegram'
  | 'spotify'
  | 'twitch'
  | 'generic'

export type SmmMetric = 'followers' | 'subscribers' | 'likes' | 'views' | 'members' | 'comments'

export type SmmPanelId = 'morethanpanel' | 'skaiyoh'

export type SmmTierId = 'elite' | 'standard' | 'budget'

export type SmmTierConfig = {
  id: SmmTierId
  label: string
  blurb: string
  dropRate: string
  refillDays: number
  servicePicker: SmmServicePickerConfig
  packages: SmmPackage[]
  trustBadges: SmmTrustBadge[]
  startTime?: string
  deliverySpeed?: string
  dripFeed?: boolean
}

export type SmmServicePickerConfig = {
  /** `cheapest_rules` picks lowest-rate eligible service across configured panels. */
  mode: 'pinned' | 'cheapest_rules'
  /** Panels to search (default: product supplier or both for `cheapest`). */
  panels?: SmmPanelId[]
  /** Per-panel service ID safelist (empty / omitted = keyword match only on that panel). */
  allowedByPanel?: Partial<Record<SmmPanelId, number[]>>
  /** Legacy single-panel allowlist (MoreThanPanel when supplier is `cheapest`). */
  allowedServiceIds?: number[]
  requireRefill?: boolean
  nameKeywords?: string[]
  categoryKeywords?: string[]
  /** Skip services whose name/category contains any of these (e.g. bot, fake). */
  excludeNameKeywords?: string[]
  /** Prefer services matching these; if any match, ignore non-matching (still picks cheapest). */
  qualityNameKeywords?: string[]
}

export type SmmProductConfig = {
  /** `cheapest` = lowest wholesale on MoreThanPanel + SkaiYoh at order time. */
  supplier: 'morethanpanel' | 'skaiyoh' | 'cheapest' | 'smmraja'
  /** Social platform — drives link validation, UI labels, and default Raja catalog filters. */
  platform: SmmPlatform
  /** What is being sold (followers, subscribers, views, …). */
  metric: SmmMetric
  serviceId: number
  tierLabel: string
  supplierCostPer1kUsd?: number
  servicePicker?: SmmServicePickerConfig
  /** Optional overrides for the target input on the product page. */
  targetInput?: { label: string; placeholder: string; hint: string }
  packages: SmmPackage[]
  trustBadges: SmmTrustBadge[]
  startTime: string
  deliverySpeed: string
  refillDays: number
  geo: string
  dripFeed: boolean
  dropRate: string
  minQuantity: number
  maxQuantity: number
  publicProfileWarning: string
  customerNotes: string[]
  /** Optional quality tiers — buyer picks one before package size. */
  tiers?: SmmTierConfig[]
  defaultTierId?: SmmTierId
}

export interface Product {
  id: number
  name: string
  image: string
  description: string
  shortDescription: string
  price: number
  /** When set above `price`, shown as struck-through “was” price (sales / admin discount). */
  compareAtPrice?: number
  category: Category
  badge?: 'bestseller' | 'popular' | 'new' | 'limited' | 'recommended' | 'elite'
  stock: number
  rating: number
  reviewCount: number
  variants?: Variant[]
  deliveryInfo: string
  features: string[]
  faqs?: { q: string; a: string }[]
  /** When true, hidden from shop / home grids but still reachable by URL and checkout id. */
  hideFromShop?: boolean
  /** Shop card opens a tier picker instead of going straight to PDP. */
  bundlePicker?: {
    title: string
    description?: string
    tiers: BundlePickerTier[]
  }
  /** SMM growth product — package picker + supplier fulfillment. */
  smmConfig?: SmmProductConfig
  /** Custom Robux amount — buyer enters desired Robux; gamepass price is calculated. */
  robuxConfig?: RobuxProductConfig
  /** Custom Discord server boost count — tiered USD pricing. */
  serverBoostConfig?: ServerBoostProductConfig
}

export type ServerBoostProductConfig = {
  minBoosts: number
  maxBoosts: number
  tiers: { boosts: number; priceUsd: number }[]
  /** Cart line + checkout metadata (e.g. "1 Month", "3 Months"). */
  durationLabel?: string
  durationMonths?: 1 | 3
}

export type RobuxProductConfig = {
  /** USD charged per 1 Robux received (e.g. 0.005 = $5 per 1,000). */
  pricePerRobuxUsd: number
  minRobux: number
  maxRobux: number
  /** Roblox platform fee (default 0.30 = 30%). */
  platformTaxRate?: number
  /** @deprecated Prefer platformTaxRate. Share credited to seller (0.7 when tax is 30%). */
  creatorShare?: number
}

export interface Variant {
  label: string
  value: string
  priceModifier?: number
}

export type Category =
  | 'discord'
  | 'streaming'
  | 'gaming'
  | 'social'
  | 'tiktok'
  | 'instagram'
  | 'youtube'
  | 'software'
  | 'zenith_lifetime'
  | 'zenith_monthly'
  | 'alchemy_lifetime'
  | 'alchemy_token'
  | 'alchemy_30_days'

export interface CategoryInfo {
  id: Category
  label: string
  icon: string
  description: string
}

export const categories: CategoryInfo[] = [
  {
    id: 'discord',
    label: 'Discord Products',
    icon: 'discord',
    description: 'Nitro, boosts, aged accounts & more',
  },
  {
    id: 'streaming',
    label: 'Streaming & media',
    icon: 'streaming',
    description: 'Netflix, Disney+, Crunchyroll, Spotify shared access & more',
  },
  {
    id: 'gaming',
    label: 'Gaming & Roblox',
    icon: 'gaming',
    description: 'Robux, Fortnite, Clash Royale, Xbox & more',
  },
  {
    id: 'social',
    label: 'Social media',
    icon: 'social',
    description: 'Instagram, Twitch growth & more',
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    icon: 'tiktok',
    description: 'Real followers with drip-feed & refill protection',
  },
  {
    id: 'instagram',
    label: 'Instagram',
    icon: 'instagram',
    description: 'Real followers with refill protection',
  },
  {
    id: 'youtube',
    label: 'YouTube',
    icon: 'youtube',
    description: 'Channel subscribers with steady delivery',
  },
  {
    id: 'software',
    label: 'Software & tools',
    icon: 'software',
    description: 'VPN, AI, creative apps & digital tools',
  },
  {
    id: 'zenith_lifetime',
    label: 'Zenith Lifetime',
    icon: 'streaming',
    description: 'Zenith products with shared access term',
  },
  {
    id: 'zenith_monthly',
    label: 'Zenith Monthly',
    icon: 'streaming',
    description: 'Zenith products with monthly term',
  },
  {
    id: 'alchemy_lifetime',
    label: 'Alchemy Lifetime',
    icon: 'streaming',
    description: 'Alchemy products with shared access term',
  },
  {
    id: 'alchemy_token',
    label: 'Alchemy Token',
    icon: 'software',
    description: 'Alchemy token-based products',
  },
  {
    id: 'alchemy_30_days',
    label: 'Alchemy 30 Days',
    icon: 'software',
    description: 'Alchemy products with 30-day term',
  },
]

/** Partner-site categories (Zenith / Alchemy) — never sold on blkvault.cc; revenue tracks them separately. */
export const PARTNER_ONLY_CATEGORIES = new Set<Category>([
  'zenith_lifetime',
  'zenith_monthly',
  'alchemy_lifetime',
  'alchemy_token',
  'alchemy_30_days',
])

export function isPartnerOnlyCategory(category: Category): boolean {
  return PARTNER_ONLY_CATEGORIES.has(category)
}

/** Partner-only category ids: kept for admin + labels, not shown on public shop/home browse UI. */
const SHOP_HIDDEN_CATEGORIES = new Set<Category>([...PARTNER_ONLY_CATEGORIES])

/** Category tabs on `/shop` and the home “Categories” grid (excludes partner-specific rows). */
export const shopBrowseCategories: CategoryInfo[] = categories.filter(
  (c) => !SHOP_HIDDEN_CATEGORIES.has(c.id),
)

const RAW_PRODUCTS = `
Chatgpt Shared Access|6.99
Youtube Premium FA (1 Month)|3.99
Youtube Premium FA (Family Owner)|7.99
Youtube Premium Shared Access FA|2.99
Disney+ Shared Access|1.99
Disney+ Shared Access + HULU|1.99
Disney+ Shared Access + HULU + ESPN+|1.99
Crunchyroll Shared Access (Fan)|1.99
Crunchyroll Shared Access (Mega)|1.99
DAZN Shared Access (Random)|1.99
DAZN Shared Access (Italy)|1.99
DAZN Shared Access (Premium ES)|2.99
DAZN Shared Access (Germany)|1.99
DAZN Shared Access (Motor ES)|1.99
DAZN Shared Access (Futbol ES)|2.99
Netflix Shared Access|2.99
Netflix Ads - VPN|1.99
Netflix No Ads - No VPN|1.99
Netflix (FA) 4K Premium|9.99
Netflix Key Access (Shared Access)|5.00
Capcut Shared Access|1.99
Prime Video (1 Month)|2.99
Prime Video Shared Access|2.99
Express VPN Shared Access|1.99
IP Vanish Shared Access|1.99
UFC Shared Access|1.99
NBA Shared Access|1.99
Paramount+ Shared Access (EU)|1.99
Paramount+ Shared Access (USA)|1.99
Duolingo Super Shared Access|1.99
Viki Shared Access|1.99
Wondershare Shared Access (Filmora)|1.99
Nord VPN Shared Access|2.99
HboMax Shared Access|1.99
Discord Random Decoration|3.99
EpicGames NFA (350+ Games)|2.99
EpicGames NFA (200-350 Games)|1.99
EpicGames NFA (50-100 Games)|1.99
Epic Games NFA (20-50 Games)|1.99
Epic Games FA (200-350 Games)|3.99
Epic Games FA (100-250 Games)|2.99
Epic Games FA (350+ Games)|7.99
Tiktok 1k+ Followers|7.99
TodTV|1.99
DAZN (Spain)|1.99
DAZN (France)|1.99
DAZN (UK)|1.99
DAZN (US)|1.99
Polsat Go Box Shared Access|1.99
CDA Shared Access|1.99
Instagram 1k+ Followers|7.99
Tunnelbear Shared Access|1.99
PureFlix|1.99
Twitch Account (2k+ Followers)|2.99
FC 26|23.99
Famas.xyz (FiveM Software Shared Access)|59.99
Famas.xyz (FiveM Software 30 Days)|25.99
Famas.xyz (FiveM Software 7 Days)|12.99
Fortnite Acc 20-50 Skins|2.99
Fortnite Leviathan Axe 100-200 Skins|23.99
Fortnite 100-150 Skins (Take The L)|7.99
Fortnite 10-20 Skins|1.99
Fortnite 2-10 Skins|1.99
Fortnite 250-400 Skins|31.99
Fortnite 200-400 Skins|17.99
Fortnite 50-100 Skins|3.99
Fortnite 60-150 (The Reaper)|4.99
Fortnite 100-250 Skins|26.99
Fortnite 400-550 Skins|71.99
Fortnite Take The L 100-250 Skins|26.99
Fortnite 60-250 Skins (Black Knight)|23.99
Fortnite Minty Axe 100-250 Skins|28.99
Fortnite Travis Scott 100-250 Skins|55.99
Fortnite Black Knight 60-200 Skins|110.99
V-Bucks 2500-5000|22.99
V-Bucks 1000-2500|7.99
Nitro Promo Code (3 Months)|2.99
Nitro Promo Code (1 Month)|1.99
Discord Verified Tokens (Email Verified)|1.99
Discord Server Boost 14x (3 Months)|8.99
Spotify Shared Access (3 Months)|3.99
Spotify Shared Access (Extended)|10.00
Chatgpt Method/Tools|39.99
Steam Shared Access: GTA|1.99
Steam Shared Access: Schedule 1|1.99
Steam Shared Access: Raft|1.99
Steam Shared Access: Forza Horizon 5|1.99
Steam Shared Access: Sons of the Forest|1.99
Steam Shared Access: The Forest|1.99
Steam Shared Access: Ready or Not|2.99
Steam Shared Access: Wallpaper Engine|1.99
Steam Shared Access: FC 25|1.99
Steam Shared Access: Among Us|1.99
Steam Shared Access: BeamNG.drive|1.99
Steam Shared Access: Euro Truck Simulator 2|1.99
Steam Shared Access: Assetto Corsa|1.99
Steam Shared Access: Cyberpunk 2077|1.99
Steam Shared Access: Resident Evil Village|1.99
Steam Shared Access: Phasmophobia|1.99
Steam Shared Access: Supermarket Simulator|1.99
Steam Shared Access: SnowRunner|1.99
Steam Shared Access: Mortal Kombat 1|1.99
Steam Shared Access: Hogwarts Legacy|1.99
Steam Shared Access: Farming Simulator 25|1.99
Steam Shared Access: PC Building Simulator|1.99
Steam Shared Access: Dead by Daylight|1.99
Steam Shared Access: House Flipper 2|1.99
Steam Shared Access: Garrys Mod|1.99
Steam Shared Access: Payday 3|1.99
Steam Shared Access: Farming Simulator 2022|1.99
Steam Shared Access: Outlast|1.99
Steam Shared Access: My Summer Car|1.99
Steam Shared Access: Green Hell|1.99
Steam Shared Access: Arma 3|1.99
Steam Shared Access: Car For Sale Simulator|1.99
Steam Shared Access: My Winter Car|1.99
Steam Shared Access: Arc Raiders|1.99
Steam Shared Access: Just Cause 4|1.99
Steam Shared Access: Scrap Mechanic|1.99
Steam Shared Access: The Witcher 3|1.99
Steam Shared Access: Thief Simulator 2|1.99
Steam Shared Access: Outlast 2|1.99
Steam Shared Access: Bloons TD 6|1.99
Steam Shared Access: Builder Simulator|1.99
Steam Shared Access: Marvel Rivals|1.99
Steam Shared Access: Detroit|1.99
Canva Premium Shared Access|2.99
Wondershare Filmora|1.99
Prime Video 6 Months FA|4.99
`.trim()

function detectCategory(name: string): Category {
  const n = name.toLowerCase()
  if (n.includes('discord') || n.includes('nitro')) return 'discord'
  if (
    n.includes('netflix') ||
    n.includes('youtube') ||
    n.includes('dazn') ||
    n.includes('disney') ||
    n.includes('crunchyroll') ||
    n.includes('prime video') ||
    n.includes('spotify') ||
    n.includes('hbomax') ||
    n.includes('todtv') ||
    n.includes('paramount') ||
    n.includes('viki') ||
    n.includes('ufc') ||
    n.includes('nba') ||
    n.includes('polsat') ||
    n.includes('cda') ||
    n.includes('pureflix')
  ) return 'streaming'
  if (n.includes('tiktok') && n.includes('follower')) return 'tiktok'
  if (n.includes('instagram') && n.includes('follower')) return 'instagram'
  if (n.includes('youtube') && (n.includes('subscriber') || n.includes('subscribers'))) return 'youtube'
  if (
    n.includes('followers') ||
    n.includes('tiktok') ||
    n.includes('instagram') ||
    n.includes('twitch account')
  ) return 'social'
  if (
    n.includes('vpn') ||
    n.includes('chatgpt') ||
    n.includes('capcut') ||
    n.includes('duolingo') ||
    n.includes('wondershare') ||
    n.includes('canva') ||
    n.includes('famas.xyz') ||
    n.includes('tunnelbear')
  ) return 'software'
  return 'gaming'
}

/** Used for shop badges and home “Featured deals” picks. */
function productBadge(name: string): Product['badge'] | undefined {
  const n = name.toLowerCase()
  const bestseller =
    n.includes('spotify shared access') ||
    n.includes('chatgpt shared access') ||
    n.includes('netflix shared access') ||
    n.includes('nitro promo code') ||
    n.includes('discord server boost')
  if (bestseller) return 'bestseller'
  const popular =
    n.includes('youtube premium') ||
    (n.includes('disney+ shared access') && !n.includes('hulu')) ||
    n.includes('prime video shared access') ||
    n.includes('discord verified tokens') ||
    n.includes('netflix ads') ||
    n.includes('netflix no ads')
  if (popular) return 'popular'
  return undefined
}

function toProduct(row: string, idx: number): Product | null {
  const [rawName, rawPrice] = row.split('|')
  const name = rawName?.trim()
  const price = Number(rawPrice)
  if (!name || !Number.isFinite(price)) return null
  if (name.toLowerCase() === 'pornhub premium shared access') return null
  if (/famas\.xyz/i.test(name)) return null
  const category = detectCategory(name)
  const disabled = /\bdazn\b/i.test(name)
  const id = idx + 1
  const badge = productBadge(name)
  const base: Product = {
    id,
    name,
    image: productImagePath(name),
    description: `${name} digital delivery. After checkout, support shares fulfillment details and confirms delivery status.`,
    shortDescription: `${name} — $${price.toFixed(2)}`,
    price,
    category,
    ...(badge && !disabled ? { badge } : {}),
    stock: disabled ? 0 : 999,
    rating: 4.6 + ((id % 5) * 0.05),
    reviewCount: 120 + id * 7,
    deliveryInfo: 'Manual delivery after payment verification',
    features: ['Fast support response', 'Status tracking', 'Replacement support window'],
    ...(disabled ? { hideFromShop: true } : {}),
  }

  if (disabled) return base

  if (name === 'Prime Video 6 Months FA') {
    return {
      ...base,
      badge: 'new',
      description: [
        'You get a standalone Amazon login with Prime Video already entitled—think of it as a six-month runway on a personal-tier account, not a shared “family slot” lottery.',
        'Before anything ships, we sanity-check sign-in and that the video catalog actually resolves, so you are not guessing whether the login is dead on arrival.',
        'Delivery is the full credential set you need to take ownership: sign in, rotate the password, and treat inbox recovery like any account you actually run.',
        'Billing on these listings is a single upfront charge from us—no recurring subscription fee collected here. What Amazon does on the underlying account is outside our control; do not attach your own card or change payment rails unless you understand the risk.',
        'If handoff is broken or access fails inside the support window published with your order, open a ticket—we troubleshoot first, then replace when the situation matches our policy.',
      ].join(' '),
      shortDescription: 'Half-year Prime Video on a full-access FA login — one payment, $4.99.',
      deliveryInfo: 'Credentials sent after payment clears. Rotate your password immediately; leave Amazon billing and payment methods alone unless you accept losing entitlement.',
      features: [
        'Prime Video access confirmed working before we send the login',
        'Roughly six months of paid window left on the account you receive',
        'Pre-flight checks on each credential pair to cut dead-on-arrival rates',
        'Full-account handoff so you control sign-in, recovery, and device limits',
        'Clean enough quality for personal use or resale where that fits your model',
        'Six-month storefront support window from delivery for eligible issues',
        'Human helpdesk path—replacement or fix when the case matches policy',
      ],
      faqs: [
        {
          q: 'What exactly is included?',
          a: 'A full-access FA-style Amazon account with Prime Video active for the advertised period. You receive login details and are expected to secure the account yourself after delivery.',
        },
        {
          q: 'Can I change the password or recovery email?',
          a: 'You should change the password right away. Avoid experimenting with payment methods or subscription billing tied to how access is funded—those edits are the fastest way to lose the slot.',
        },
        {
          q: 'What if it stops working mid-term?',
          a: 'Contact support with your order reference. We review what failed and, when it falls under our replacement terms for this SKU, we move you to a fix or swap. Issues caused by billing edits or Amazon-side enforcement may not qualify.',
        },
      ],
    }
  }

  if (name === 'Netflix Key Access (Shared Access)') {
    return {
      ...base,
      compareAtPrice: 10.00,
      badge: 'new',
      description: [
        'Guaranteed Netflix Access — Fully working subscribed accounts.',
        'Long-Lasting Access — Accounts designed for extended use with auto-renewal.',
        'Unlimited Replacements — If an account stops working, redeem your key again for a new one instantly.',
        'PC & TV Supported — Works perfectly on PC and TV; mobile login supported with limited playback.',
        'Key replacement program — Redeem your key for fresh shared-account credentials while the program remains active.',
      ].join(' '),
      shortDescription: 'Netflix key access (shared access) — one key, unlimited replacements, $5.00.',
      deliveryInfo: 'Your unique key is delivered instantly after payment. Redeem it anytime to receive fresh Netflix credentials.',
      features: [
        'Guaranteed Netflix Access — Fully working subscribed accounts',
        'Long-Lasting Access — Accounts designed for extended use with auto-renewal',
        'Unlimited Replacements — If an account stops working, redeem your key again for a new one instantly',
        'PC & TV Supported — Works perfectly on PC and TV; mobile login supported with limited playback',
        'Key replacement program — Unlimited redeems for new credentials while supported',
      ],
      faqs: [
        {
          q: 'How does the key work?',
          a: 'After purchase you receive a unique key. Use it to redeem Netflix account credentials. If the account ever stops working, simply redeem the key again for a fresh replacement.',
        },
        {
          q: 'Does it work on all devices?',
          a: 'It works perfectly on PC and TV. Mobile login is supported, but playback may be limited on some mobile devices.',
        },
        {
          q: 'What if my account stops working?',
          a: 'Redeem your key again for a fresh login while replacements are supported. No need to open a ticket for standard key swaps.',
        },
      ],
    }
  }

  if (name === 'Tiktok 1k+ Followers' || name === 'Instagram 1k+ Followers') {
    return { ...base, hideFromShop: true }
  }

  if (isSharedAccessListing(name) && !usesCustomSharedAccessCopy(name)) {
    const shared = buildSharedAccessProductCopy(name, price)
    return {
      ...base,
      description: shared.description,
      shortDescription: shared.shortDescription,
      deliveryInfo: shared.deliveryInfo,
      features: shared.features,
      faqs: shared.faqs,
    }
  }

  if (name === 'Youtube Premium Shared Access FA') {
    return {
      ...base,
      description: [
        'YouTube Premium on a premium-grade personal account — smooth playback, background listening, and downloads without the usual interruptions.',
        'You receive the Gmail credentials tied to the account so you can sign in, secure it with your own password, and manage recovery like any normal inbox.',
        'This is a private solo account — not a crowded “shared” login. Treat it like your own subscription.',
        'To keep access stable, do not change billing details, saved payment methods, or anything tied to how the subscription is paid. Support can’t fix accounts broken by billing edits.',
        'There is no warranty if Google revokes access or bans the account. By purchasing, you accept that replacements are not offered in those cases.',
      ].join(' '),
      deliveryInfo:
        'Login details sent after payment clears. Change your password right away — leave billing and payment settings untouched.',
      features: [
        'Premium-grade personal account — ad-free viewing, background play, and downloads the way Premium is meant to feel',
        'Full Gmail access — you control the inbox and can update the password after delivery',
        'Private, solo use — the account is yours; we don’t hand out shared public logins',
        'Leave billing alone — don’t edit payment methods or subscription billing; changes can kill access',
        'No revoke or ban coverage — if Google pulls the account, we don’t replace it',
      ],
      faqs: [
        {
          q: 'Can I change the email password?',
          a: 'Yes. You’re meant to take over the Gmail and set a strong password you control. Just avoid touching billing or payment info on the account.',
        },
        {
          q: 'What if the account stops working?',
          a: 'These listings don’t include a replacement if Google revokes Premium or restricts the account. Contact support for delivery issues only — not for post-delivery platform bans.',
        },
      ],
    }
  }

  return base
}

const products: Product[] = RAW_PRODUCTS
  .split('\n')
  .map((line, i) => toProduct(line, i))
  .filter((p): p is Product => p !== null)

/** Custom-amount Robux storefront listing. */
export const ROBUX_PRODUCT_ID = 130

/** SMM storefront product ids. */
export const TIKTOK_REAL_FOLLOWERS_PRODUCT_ID = 131
export const INSTAGRAM_REAL_FOLLOWERS_PRODUCT_ID = 132
export const YOUTUBE_SUBSCRIBERS_PRODUCT_ID = 133

const tiktokSmmConfig: SmmProductConfig = (() => {
  const elitePackages = [
    { label: '100 followers', quantity: 100, price: 1.99 },
    { label: '250 followers', quantity: 250, price: 4.49 },
    { label: '500 followers', quantity: 500, price: 7.99 },
    { label: '1,000 followers', quantity: 1000, price: 12.99 },
    { label: '5,000 followers', quantity: 5000, price: 59.99 },
    { label: '10,000 followers', quantity: 10000, price: 109.99 },
  ]
  const tiers = buildFollowerTiers({
    elitePackages,
    elitePicker: { mode: 'cheapest_rules', panels: ['morethanpanel', 'skaiyoh'] },
    platformKeywords: { name: ['tiktok', 'follower'], category: ['tiktok'] },
    startTime: '0–6 hours',
    deliverySpeed: '10k–20k / day',
    dripFeed: true,
    eliteTrust: [
      { label: 'Elite Service' },
      { label: 'Global' },
      { label: 'Drip-Feed ON' },
      { label: '30 Day Refill' },
      { label: 'Low Drop' },
    ],
    standardTrust: [{ label: 'Standard' }, { label: 'Global' }, { label: '15 Day Refill' }],
    budgetTrust: [{ label: 'Budget' }, { label: 'Global' }, { label: 'Best Value' }],
  })
  return {
    supplier: 'cheapest',
    platform: 'tiktok',
    metric: 'followers',
    serviceId: 9054,
    tierLabel: 'Elite',
    supplierCostPer1kUsd: 2.52,
    packages: elitePackages,
    trustBadges: tiers[0]!.trustBadges,
    startTime: '0–6 hours',
    deliverySpeed: '10k–20k / day',
    refillDays: 30,
    geo: 'Global',
    dripFeed: true,
    dropRate: 'Low',
    minQuantity: 10,
    maxQuantity: 1_000_000,
    publicProfileWarning:
      'Your TikTok profile must be public for the order to start. You can make it private again after completion. Do not change your username while the order is processing.',
    customerNotes: [
      'Choose Elite for the lowest drop and longest refill window, or Budget to save.',
      'Global audience · delivery speed varies by tier.',
    ],
    tiers,
    defaultTierId: 'elite',
  }
})()

const tiktokRealFollowersProduct: Product = {
  id: TIKTOK_REAL_FOLLOWERS_PRODUCT_ID,
  name: 'TikTok Real Followers',
  image: productImagePath('TikTok Real Followers'),
  description:
    'Premium TikTok followers with drip-feed enabled, global delivery, and tiered pricing. Elite tier targets the lowest drop and longest refill; Budget saves more with higher drop risk.',
  shortDescription: `TikTok followers — Elite, Standard & Budget · from $${minSmmPackagePrice(tiktokSmmConfig).toFixed(2)}`,
  price: minSmmPackagePrice(tiktokSmmConfig),
  category: 'tiktok',
  badge: 'elite',
  stock: 999,
  rating: 4.88,
  reviewCount: 412,
  deliveryInfo: 'Starts in 0–6 hours · drip-feed delivery · 10k–20k followers per day',
  features: [
    'Three tiers: Elite, Standard, and Budget',
    'Pick your balance of price vs drop risk',
    'Global geo · drip-feed on Elite',
    'Refill protection on Elite & Standard (when eligible)',
    'Profile must be public until the order completes',
  ],
  faqs: [
    {
      q: 'What is the difference between Elite, Standard, and Budget?',
      a: 'Elite uses our strictest service filters (lowest drop, 30-day refill). Standard is cheaper with moderate drop risk. Budget is the lowest price with higher drop and no refill guarantee — best for testing.',
    },
    {
      q: 'Why does my profile need to be public?',
      a: 'The delivery system cannot start on a private account. Once the order completes you can switch back to private. Do not change your username while processing.',
    },
    {
      q: 'What does drip-feed mean?',
      a: 'Followers are added gradually instead of all at once, which looks more natural and reduces drop risk.',
    },
    {
      q: 'How does the 30-day refill work?',
      a: 'If followers drop within the refill window, contact support with your order ID and we can request a refill when eligible.',
    },
  ],
  smmConfig: tiktokSmmConfig,
}

const instagramSmmConfig: SmmProductConfig = (() => {
  const elitePackages = [
    { label: '100 followers', quantity: 100, price: 2.49 },
    { label: '250 followers', quantity: 250, price: 5.49 },
    { label: '500 followers', quantity: 500, price: 9.99 },
    { label: '1,000 followers', quantity: 1000, price: 17.99 },
    { label: '5,000 followers', quantity: 5000, price: 79.99 },
    { label: '10,000 followers', quantity: 10000, price: 149.99 },
  ]
  const tiers = buildFollowerTiers({
    elitePackages,
    elitePicker: { mode: 'cheapest_rules', panels: ['morethanpanel', 'skaiyoh'] },
    platformKeywords: {
      name: ['instagram', 'follower'],
      category: ['instagram'],
      extraExclude: ['reel', 'story'],
    },
    startTime: '0–12 hours',
    deliverySpeed: 'Up to 5k / day',
    dripFeed: false,
    eliteTrust: [{ label: 'Elite Service' }, { label: 'Global' }, { label: '30 Day Refill' }, { label: 'Low Drop' }],
    standardTrust: [{ label: 'Standard' }, { label: 'Global' }],
    budgetTrust: [{ label: 'Budget' }, { label: 'Best Value' }],
  })
  return {
    supplier: 'cheapest',
    platform: 'instagram',
    metric: 'followers',
    serviceId: 1,
    tierLabel: 'Elite',
    packages: elitePackages,
    trustBadges: tiers[0]!.trustBadges,
    startTime: '0–12 hours',
    deliverySpeed: 'Up to 5k / day',
    refillDays: 30,
    geo: 'Global',
    dripFeed: false,
    dropRate: 'Low',
    minQuantity: 10,
    maxQuantity: 500_000,
    publicProfileWarning:
      'Your Instagram profile must be public for the order to start. You can make it private again after completion. Do not change your username while the order is processing.',
    customerNotes: ['Three tiers — pick the balance of price and retention that fits you.'],
    tiers,
    defaultTierId: 'elite',
  }
})()

const instagramRealFollowersProduct: Product = {
  id: INSTAGRAM_REAL_FOLLOWERS_PRODUCT_ID,
  name: 'Instagram Real Followers',
  image: productImagePath('Instagram Real Followers'),
  description:
    'Instagram followers with global delivery and tiered pricing. Elite for lowest drop and refill; Budget for the lowest price with higher drop risk.',
  shortDescription: `Instagram followers — Elite, Standard & Budget · from $${minSmmPackagePrice(instagramSmmConfig).toFixed(2)}`,
  price: minSmmPackagePrice(instagramSmmConfig),
  category: 'instagram',
  badge: 'elite',
  stock: 999,
  rating: 4.86,
  reviewCount: 328,
  deliveryInfo: 'Starts in 0–12 hours · gradual delivery · up to 5k followers per day',
  features: [
    'Three tiers: Elite, Standard, and Budget',
    'Global geo · natural delivery pace',
    'Refill on Elite & Standard when eligible',
    'Profile must be public until the order completes',
  ],
  faqs: [
    {
      q: 'Which tier should I choose?',
      a: 'Elite filters for premium refill-backed services. Standard is mid-range. Budget is cheapest with higher drop and no refill guarantee.',
    },
    {
      q: 'Why does my profile need to be public?',
      a: 'Delivery cannot start on a private account. Once the order completes you can switch back to private. Do not change your username while processing.',
    },
    {
      q: 'Will this hurt my account?',
      a: 'We use high-quality tiers with gradual delivery to reduce risk. Avoid buying multiple overlapping follower orders on the same profile at once.',
    },
    {
      q: 'How does the 30-day refill work?',
      a: 'If followers drop within the refill window, contact support with your order ID and we can request a refill when eligible.',
    },
  ],
  smmConfig: instagramSmmConfig,
}

const youtubeSmmConfig: SmmProductConfig = (() => {
  const elitePackages = [
    { label: '100 subscribers', quantity: 100, price: 3.99 },
    { label: '250 subscribers', quantity: 250, price: 8.99 },
    { label: '500 subscribers', quantity: 500, price: 15.99 },
    { label: '1,000 subscribers', quantity: 1000, price: 28.99 },
    { label: '2,500 subscribers', quantity: 2500, price: 64.99 },
    { label: '5,000 subscribers', quantity: 5000, price: 119.99 },
  ]
  const tiers = buildFollowerTiers({
    elitePackages,
    elitePicker: { mode: 'cheapest_rules', panels: ['morethanpanel', 'skaiyoh'] },
    platformKeywords: {
      name: ['youtube', 'subscriber'],
      category: ['youtube'],
      extraExclude: ['view', 'like', 'watch hour', 'comment', 'live stream', 'short', 'share'],
    },
    startTime: '0–24 hours',
    deliverySpeed: 'Varies by tier',
    dripFeed: true,
    eliteTrust: [{ label: 'Elite Service' }, { label: 'Global' }, { label: 'Drip-Feed ON' }, { label: 'Refill' }],
    standardTrust: [{ label: 'Standard' }, { label: 'Global' }],
    budgetTrust: [{ label: 'Budget' }, { label: 'Best Value' }],
  })
  return {
    supplier: 'cheapest',
    platform: 'youtube',
    metric: 'subscribers',
    serviceId: 1,
    tierLabel: 'Elite',
    packages: elitePackages,
    trustBadges: tiers[0]!.trustBadges,
    startTime: '0–24 hours',
    deliverySpeed: 'Varies by tier',
    refillDays: 30,
    geo: 'Global',
    dripFeed: true,
    dropRate: 'Low–medium',
    minQuantity: 10,
    maxQuantity: 100_000,
    publicProfileWarning:
      'Your YouTube channel must be public for the order to start. Do not change your channel URL or @handle while the order is processing.',
    customerNotes: ['Three tiers — Elite for refill-backed services, Budget for lowest cost.'],
    tiers,
    defaultTierId: 'elite',
  }
})()

const youtubeSubscribersProduct: Product = {
  id: YOUTUBE_SUBSCRIBERS_PRODUCT_ID,
  name: 'YouTube Subscribers',
  image: productImagePath('YouTube Subscribers'),
  description:
    'Real YouTube subscribers with drip-feed delivery, global reach, and refill protection on eligible tiers. Ideal for channel credibility and steady growth.',
  shortDescription: `YouTube subscribers — Elite, Standard & Budget · from $${minSmmPackagePrice(youtubeSmmConfig).toFixed(2)}`,
  price: minSmmPackagePrice(youtubeSmmConfig),
  category: 'youtube',
  badge: 'elite',
  stock: 999,
  rating: 4.84,
  reviewCount: 256,
  deliveryInfo: 'Starts in 0–24 hours · drip-feed delivery · speed varies by package',
  features: [
    'Three tiers: Elite, Standard, and Budget',
    'Global geo · drip-feed on Elite',
    'Refill on Elite & Standard when eligible',
    'Channel must be public until the order completes',
  ],
  faqs: [
    {
      q: 'What link should I use?',
      a: 'Paste your @handle or full channel URL (youtube.com/@yourchannel). Do not change the channel handle or custom URL while the order is running.',
    },
    {
      q: 'How fast will subscribers arrive?',
      a: 'Delivery is gradual (drip-feed) so growth looks natural. Most orders start within 24 hours and complete over several days depending on package size.',
    },
    {
      q: 'Do you guarantee retention?',
      a: 'Eligible tiers include refill protection within the stated window. Contact support with your order ID if the count drops unexpectedly.',
    },
  ],
  smmConfig: youtubeSmmConfig,
}

const robuxProduct: Product = {
  id: ROBUX_PRODUCT_ID,
  name: 'Robux',
  image: productImagePath('Robux'),
  description: [
    'Buy the Robux amount you need — enter how much you want to receive after Roblox’s platform fee.',
    'We show the exact gamepass price to set, then you paste your gamepass link at checkout so we can purchase it after payment.',
    'Delivery is usually within minutes once your gamepass is purchased.',
  ].join(' '),
  shortDescription: 'From $5.00 per 1,000 Robux — enter any amount up to 10,000.',
  price: 5,
  category: 'gaming',
  badge: 'popular',
  stock: 999,
  rating: 4.85,
  reviewCount: 1840,
  deliveryInfo:
    'Create a gamepass at the listed price, paste its Roblox link when ordering, and we purchase it after payment clears.',
  features: [
    'Choose any amount up to 10,000 Robux',
    'Required gamepass link on every order',
    'Live calculator: after-tax Robux, gamepass price, and USD total',
    'Roblox 30% tax on gamepass sales included in the calculator',
    'Fast delivery after gamepass purchase',
    'Support if credentials or delivery need help',
  ],
  faqs: [
    {
      q: 'How does the calculator work?',
      a: 'Enter the Robux you want to receive after Roblox’s 30% fee. We compute the gamepass price to set (rounded up) and your USD total.',
    },
    {
      q: 'Why is the gamepass price higher than the Robux I want?',
      a: 'Roblox keeps about 30% of gamepass sales. Setting a higher gamepass price is how sellers deliver the Robux amount you chose.',
    },
    {
      q: 'What is the maximum per order?',
      a: '10,000 Robux per checkout on this listing.',
    },
    {
      q: 'Why do you need my gamepass link?',
      a: 'We purchase your gamepass after payment. Create it at the calculator price, set it for sale, then paste the roblox.com/game-pass/… link when you order.',
    },
  ],
  robuxConfig: {
    pricePerRobuxUsd: 0.005,
    minRobux: 1,
    maxRobux: 10_000,
    platformTaxRate: 0.3,
  },
}

const spotify1MonthSharedCopy = buildSharedAccessProductCopy('Spotify Shared Access (1 Month)', 1.99)

const spotify1MonthSharedProduct: Product = {
  id: SPOTIFY_1M_SHARED_PRODUCT_ID,
  name: 'Spotify Shared Access (1 Month)',
  image: productImagePath('Spotify Shared Access (1 Month)'),
  description: spotify1MonthSharedCopy.description,
  shortDescription: spotify1MonthSharedCopy.shortDescription,
  price: 1.99,
  category: 'streaming',
  stock: 999,
  rating: 4.7,
  reviewCount: 640,
  deliveryInfo: spotify1MonthSharedCopy.deliveryInfo,
  features: spotify1MonthSharedCopy.features,
  faqs: spotify1MonthSharedCopy.faqs,
}

const serverBoost1MonthProduct: Product = {
  id: SERVER_BOOST_PRODUCT_ID,
  name: 'Discord Server Boosts (1 Month)',
  image: productImagePath('Discord Server Boosts'),
  description: [
    'Discord server boosts (1 month) — choose a package: 2, 8, 14, 20, or 30 boosts.',
    'Each boost stays active on your server for 1 month. Paste your Discord invite link at checkout.',
  ].join(' '),
  shortDescription: 'Packages from $0.99 — 14× @ $4.99 unlocks Level 3 (Best Value).',
  price: 0.99,
  category: 'discord',
  badge: 'bestseller',
  stock: 999,
  rating: 4.9,
  reviewCount: 920,
  deliveryInfo: 'Boosts are applied to your server after payment clears — usually within a few hours.',
  features: [
    '⭐ Best Value: 14 boosts @ $4.99 — unlock Discord Level 3',
    'Packages: 2, 8, 14, 20, or 30 boosts',
    'Required Discord server link on every order',
  ],
  faqs: [
    {
      q: 'How does pricing work?',
      a: 'Pick one of the listed packages on the product page. 14 boosts is Best Value and unlocks Discord Level 3 ($4.99).',
    },
    {
      q: 'What Discord server link should I use?',
      a: 'A permanent discord.gg invite or your server’s discord.com/channels/… link. We cannot deliver without it. Create a never-expiring invite under Server Settings → Invites.',
    },
    {
      q: 'How long do boosts last?',
      a: 'One boost = one month of boosting on your server. Buying more boosts in one order raises your server level faster (14 boosts unlocks Level 3). If you already have boosts running, Discord extends or stacks the time automatically.',
    },
  ],
  serverBoostConfig: {
    minBoosts: 2,
    maxBoosts: 30,
    durationLabel: '1 Month',
    durationMonths: 1,
    tiers: [
      { boosts: 2, priceUsd: 0.99 },
      { boosts: 8, priceUsd: 2.99 },
      { boosts: 14, priceUsd: 4.99 },
      { boosts: 20, priceUsd: 6.99 },
      { boosts: 30, priceUsd: 9.99 },
    ],
  },
}

const serverBoost3MonthProduct: Product = {
  id: SERVER_BOOST_3M_PRODUCT_ID,
  name: 'Discord Server Boosts (3 Months)',
  image: productImagePath('Discord Server Boosts'),
  description: [
    'Discord server boosts (3 months) — choose a package: 2, 8, 14, 20, or 30 boosts.',
    'Paste your Discord server link at checkout so we can apply boosts after payment.',
  ].join(' '),
  shortDescription: 'Packages from $1.99 — 14× @ $8.99 unlocks Level 3 (Best Value).',
  price: 1.99,
  category: 'discord',
  badge: 'bestseller',
  stock: 999,
  rating: 4.9,
  reviewCount: 640,
  deliveryInfo: 'Boosts are applied to your server after payment clears — usually within a few hours.',
  features: [
    '⭐ Best Value: 14 boosts @ $8.99 — unlock Discord Level 3',
    'Packages: 2, 8, 14, 20, or 30 boosts',
    'Each boost lasts 3 months on your server',
    'Required Discord server link on every order',
  ],
  faqs: [
    {
      q: 'How does pricing work?',
      a: 'Pick one of the listed packages on the product page. 14 boosts is Best Value ($8.99) and unlocks Discord Level 3.',
    },
    {
      q: 'What Discord server link should I use?',
      a: 'A permanent discord.gg invite or your server’s discord.com/channels/… link. We cannot deliver without it.',
    },
    {
      q: 'How long do boosts last?',
      a: 'One boost = three months of boosting on your server. Buying more boosts in one order raises your server level faster (14 boosts unlocks Level 3). If you already have boosts running, Discord extends or stacks the time automatically.',
    },
  ],
  serverBoostConfig: {
    minBoosts: 2,
    maxBoosts: 30,
    durationLabel: '3 Months',
    durationMonths: 3,
    tiers: [
      { boosts: 2, priceUsd: 1.99 },
      { boosts: 8, priceUsd: 5.99 },
      { boosts: 14, priceUsd: 8.99 },
      { boosts: 20, priceUsd: 12.99 },
      { boosts: 30, priceUsd: 18.99 },
    ],
  },
}

const catalogWithSmm: Product[] = applyProductFamilies([
  ...products,
  robuxProduct,
  spotify1MonthSharedProduct,
  serverBoost1MonthProduct,
  serverBoost3MonthProduct,
  tiktokRealFollowersProduct,
  instagramRealFollowersProduct,
  youtubeSubscribersProduct,
])

/** Products shown on shop and home grids (excludes tier SKUs bundled under a parent card). */
/** Hide old fixed 1k Robux SKUs (custom amount listing is #130). */
function isLegacyRobuxListing(name: string, productId: number): boolean {
  if (productId === ROBUX_PRODUCT_ID) return false
  const n = name.trim().toLowerCase()
  return (
    /robux\s*[—–-]\s*1[,.]?000/.test(n) ||
    /\b1\s*k\b.*\brobux\b/.test(n) ||
    /\brobux\b.*\b1,?000\b/.test(n) ||
    n.includes('1k robux') ||
    n === 'robux — 1,000'
  )
}

function isLegacyServerBoostListing(name: string, productId: number): boolean {
  if (SERVER_BOOST_PRODUCT_IDS.includes(productId as (typeof SERVER_BOOST_PRODUCT_IDS)[number])) return false
  const n = name.trim().toLowerCase()
  return /discord\s+server\s+boost/i.test(n) && !/boosts$/i.test(name.trim())
}

export function productsShownInShop(): Product[] {
  return catalogWithSmm.filter(
    (p) =>
      !p.hideFromShop &&
      !isPartnerOnlyCategory(p.category) &&
      !isLegacyRobuxListing(p.name, p.id) &&
      !isLegacyServerBoostListing(p.name, p.id),
  )
}

export default catalogWithSmm
